import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Page from '../components/Page';
import Logo from '../components/Logo';
import axios from 'axios'; 
import Iconify from 'src/components/Iconify';
import { LoadingButton } from '@mui/lab';
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
  backgroundImage: 'url("/static/vidisky_bg.jpg")',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
  alignItems: 'center',
}));

const CardContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  zIndex: 1, // Ensure the card is displayed above the background
  maxWidth: 450,
  width: '220%',
  top: '200px',
  left: '48%', // Center the card horizontally
  transform: 'translateX(-50%)', // Center the card horizontally
  padding: theme.spacing(4),
  backgroundColor: theme.palette.common.white, // Set background color to white
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3], // Add shadow for better visibility
  marginTop: theme.spacing(1),
  color:'#fff'
}));

// ----------------------------------------------------------------------

export default function ResetPassword() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const key = urlParams.get('key');

  const [loading, setLoading] = useState(true);
  const [isValidKey, setIsValidKey] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  // Dummy API simulation for key validation
  const validateKey = async (key) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (key === 'valid123') {
          resolve({ valid: true });
        } else {
          resolve({ valid: false });
        }
      }, 1000);
    });
  };


  const resetPasswordApi = async (key, password) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (key === 'valid123') {
          resolve({ success: true, message: 'Password reset successfully' });
        } else {
          resolve({ success: false, message: 'Invalid reset key' });
        }
      }, 1000);
    });
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

   const handleShowConfirmPassword = () => {
    setShowConfirmPassword((show) => !show);
  };


  useEffect(() => {
    if (key) {
      validateKey(key).then((res) => {
        if (res.valid) {
          setIsValidKey(true);
        } else {
          setError('Invalid or expired reset link.');
        }
        setLoading(false);
      });
    } else {
      setError('Reset key is missing.');
      setLoading(false);
    }
  }, [key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

     if (!password || !confirmPassword) {
      setError('Both password fields are required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    const res = await resetPasswordApi(key, password);

    setSubmitting(false);

    if (res.success) {
      setSuccess(res.message);
      setTimeout(() => {
        navigate('/login'); // Redirect after success
      }, 2000);
    } else {
      setError(res.message);
    }
  };

  return (
    <Page title="Reset Password">
      <RootStyle>
        <ContentStyle>
          <CardContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', }}>
              <Logo />
            </Box>
             <Box className='headers' style={{ textAlign: 'center',marginTop:'10px' }}>
              <Typography sx={{ color: '#fff' }} gutterBottom style={{ textAlign: 'center' }}>
               Reset Your Password
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 3 }}>Enter your Password below.</Typography>
            </Box>

            {loading && <Typography sx={{ textAlign: 'center' }}>Validating key...</Typography>}

            {!loading && error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loading && isValidKey && (
              <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="New Password"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{
                        input: { color: '#fff' } 
                    }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleShowPassword} edge="end">
                              <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          color: '#fff', // sets the input text color
                        },
                      }}
                />
                <TextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{
                    input: { color: '#fff' } 
                  }}
                  InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleShowConfirmPassword} edge="end">
                            <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        color: '#fff', // sets the input text color
                      },
                    }}
                />
               <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={submitting}  
                  sx={{ mt: 2 }}
                >
                  Reset Password
              </LoadingButton>

              </form>
            )}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          </CardContainer>
        </ContentStyle>
      </RootStyle>
       
    </Page>
  );
}
